import { useState, useEffect, useMemo } from 'react';
import { Search } from 'lucide-react';
import { supabase, LogEntry } from '../lib/supabase';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { usePreferences } from '../contexts/PreferencesContext';
import { DynamicIcon } from '../lib/icons';
import { ImageViewer } from '../components/ui/ImageViewer';
import { isPdfFile } from '../lib/pdfUtils';
import { FileText } from 'lucide-react';
import { CachedImage } from '../components/ui/CachedImage';
import { useTranslations } from '../i18n';
import { theme, styles } from '../lib/theme';
import { LogEntryItem } from '../components/cars/LogEntryItem';
import { SparePartItem } from '../components/cars/SparePartItem';
import { NoteItem } from '../components/cars/NoteItem';
import { GalleryItem } from '../components/cars/GalleryItem';
import { LocationItem } from '../components/cars/LocationItem';
import { ReminderItem } from '../components/cars/ReminderItem';
import { PdfViewer } from '../components/ui/PdfViewer';
import { normalizeExpenseTypeKey, getExpenseTypeNameFromData, SupportedLanguage } from '../lib/expenseTypeHelpers';
import defaultExpenseTypesData from '../data/defaultExpenseTypes.json';


type GlobalSearchViewProps = {
    carId: string;
    slug: string;
    activeTab: string;
    onClose: () => void;
    logEntries: LogEntry[];
    query: string;
    onQueryChange: (query: string) => void;
};

export function GlobalSearchView({ carId, slug, activeTab, onClose, logEntries, query: propsQuery, onQueryChange }: GlobalSearchViewProps) {
    const [localQuery, setLocalQuery] = useState(propsQuery);
    const query = localQuery;

    // Sync back to URL with debounce to avoid breaking diacritics/IME
    useEffect(() => {
        const timer = setTimeout(() => {
            if (localQuery !== propsQuery) {
                onQueryChange(localQuery);
            }
        }, 500);
        return () => clearTimeout(timer);
    }, [localQuery, onQueryChange, propsQuery]);

    const [notes, setNotes] = useState<any[]>([]);
    const [parts, setParts] = useState<any[]>([]);
    const [locations, setLocations] = useState<any[]>([]);
    const [reminders, setReminders] = useState<any[]>([]);
    const [images, setImages] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [viewerImages, setViewerImages] = useState<{ urls: string[], index: number } | null>(null);
    const [pdfViewerUrl, setPdfViewerUrl] = useState<string | null>(null);
    const navigate = useNavigate();
    const { preferences: globalPreferences } = usePreferences();
    const { t, language } = useTranslations();
    const outletContext = useOutletContext<any>() || {};
    const { selectedCar } = outletContext;
    const ownerPreferences = outletContext.ownerPreferences;

    const effectivePreferences = useMemo(() => {
        const base = ownerPreferences || globalPreferences;
        if (!base) return null;
        return {
            ...base,
            distance_unit: selectedCar?.distance_unit || base.distance_unit,
            volume_unit: selectedCar?.volume_unit || base.volume_unit,
            currency: selectedCar?.currency || base.currency,
            currency_symbol: selectedCar?.currency_symbol || base.currency_symbol
        };
    }, [ownerPreferences, globalPreferences, selectedCar]);

    useEffect(() => {
        loadGlobalData();
    }, [carId]);

    const loadGlobalData = async () => {
        setLoading(true);
        try {
            const [notesRes, partsRes, expenseImagesRes, remindersRes] = await Promise.all([
                supabase.from('notes').select(`
                    *, 
                    note_images(*), 
                    note_spare_parts(spare_part_id),
                    reminders(id, remind_date, is_active, repeat_yearly)
                `).eq('car_id', carId),
                supabase.from('spare_parts').select('*, reminders(id, remind_date, is_active, repeat_yearly)').eq('car_id', carId),
                supabase
                    .from('log_entry_images')
                    .select('*, log_entries!inner(car_id, title, expenses(type))')
                    .eq('log_entries.car_id', carId),
                supabase.from('reminders').select('*').eq('car_id', carId).is('car_task_id', null)
            ]);

            if (notesRes.data) setNotes(notesRes.data);
            if (partsRes.data) setParts(partsRes.data);
            if (remindersRes.data) setReminders(remindersRes.data);

            // Fetch and enrich user locations
            const locationsRes = await supabase
                .from('locations')
                .select('*, log_entries(date, id, expenses(type))')
                .eq('user_id', outletContext.effectiveUserId || '');

            if (locationsRes.data) {
                const enriched = (locationsRes.data || []).map((loc: any) => {
                    const sortedEntries = (loc.log_entries || [])
                        .filter((e: any) => e.expenses && e.expenses.length > 0)
                        .sort((a: any, b: any) => {
                            const dateDiff = new Date(b.date).getTime() - new Date(a.date).getTime();
                            if (dateDiff !== 0) return dateDiff;
                            return (b.created_at || "").localeCompare(a.created_at || "");
                        });

                    const lastEntry = sortedEntries[0];
                    const lastExpenseType = lastEntry?.expenses?.[0]?.type || null;
                    const entryCount = (loc.log_entries || []).length;

                    return {
                        ...loc,
                        last_expense_type: lastExpenseType,
                        entryCount
                    };
                });
                setLocations(enriched);
            }

            const getTypeName = (type: string) => {
                const key = normalizeExpenseTypeKey(type);
                const info = (defaultExpenseTypesData as any)[key];
                if (info) return getExpenseTypeNameFromData(info, language as SupportedLanguage);
                return (t.expenses.typeLabels as any)[type] || type;
            };

            const allImages = [
                ...(expenseImagesRes.data || []).map(img => {
                    const entry = img.log_entries as any;
                    const expenses = entry?.expenses || [];
                    const autoTitle = expenses.length > 0
                        ? Array.from(new Set(expenses.map((e: any) => getTypeName(e.type)))).join(', ')
                        : t.search.expenseImage;

                    const displayTitle = entry?.title || autoTitle;

                    return {
                        ...img,
                        type: 'expense',
                        source_title: displayTitle,
                        // Use broad context for search: comment + entry title (or auto-title)
                        search_text: `${img.comment || ''} ${displayTitle}`.trim()
                    };
                }),
                ...(notesRes.data || []).flatMap(note => (note.note_images || []).map((img: any) => ({
                    ...img,
                    type: 'note',
                    source_title: note.title || t.search.noteImage,
                    search_text: `${img.comment || ''} ${note.title || ''} ${note.content || ''}`.trim()
                })))
            ];
            setImages(allImages);
        } catch (error) {
            console.error('Error loading search data:', error);
        } finally {
            setLoading(false);
        }
    };

    const searchResults = useMemo(() => {
        if (!query.trim()) return { expenses: [], parts: [], notes: [], locations: [], gallery: [], reminders: [] };

        const normalize = (str: string) => 
            str.toLowerCase()
               .normalize('NFD')
               .replace(/[\u0300-\u036f]/g, "");

        const terms = query.split(/\s+/).filter((t: string) => t.length > 0).map(t => normalize(t));
        const matches = (text: string | null | undefined) => {
            if (!text) return false;
            const normalizedText = normalize(text);
            return terms.every((term: string) => normalizedText.includes(term));
        };

        const getTypeName = (type: string) => {
            const key = normalizeExpenseTypeKey(type);
            const info = (defaultExpenseTypesData as any)[key];
            if (info) return getExpenseTypeNameFromData(info, language as SupportedLanguage);
            return (t.expenses.typeLabels as any)[type] || type;
        };

        const filteredExpenses = logEntries.filter((e: LogEntry) => {
            const expenseMatches = (e.expenses || []).some(exp =>
                matches(exp.title) || matches(exp.description) || matches(exp.type) || matches(getTypeName(exp.type))
            );
            return matches(e.title) || expenseMatches;
        });

        const filteredParts = parts.filter((p: any) =>
            matches(p.name) || matches(p.part_code) || matches(p.comment)
        );

        const filteredNotes = notes.filter((n: any) =>
            matches(n.title) || matches(n.content)
        );

        const filteredLocations = locations.filter((l: any) =>
            matches(l.name) || matches(l.address) || matches(l.description)
        );

        const filteredReminders = reminders.filter((r: any) =>
            matches(r.name) || matches(r.description)
        );

        const filteredImages = images.filter((img: any) =>
            matches(img.search_text)
        ).map((img: any) => {
            let subtitle = img.source_title;
            if (img.type === 'expense') subtitle = `${t.expenses.title}: ${img.source_title}`;
            if (img.type === 'note') subtitle = `${t.notes.title}: ${img.source_title}`;
            return {
                id: img.id,
                type: 'image' as const,
                title: img.comment || (img.type === 'note' ? t.search.noteImage : t.search.expenseImage),
                subtitle: subtitle,
                date: img.created_at,
                imageUrl: img.image_url,
                thumbnail: img.image_url_thumb,
                icon: 'Image',
                color: '#8B5CF6'
            };
        });

        // Link the onClick separately to avoid issues with closure over the mapped list
        const results = {
            expenses: filteredExpenses,
            parts: filteredParts,
            notes: filteredNotes,
            locations: filteredLocations,
            gallery: filteredImages,
            reminders: filteredReminders
        };

        // For files, we filter out PDFs from image viewer and handle them separately
        const imageUrls = filteredImages.filter(img => !isPdfFile(img.imageUrl)).map(img => img.imageUrl);
        filteredImages.forEach((img) => {
            if (isPdfFile(img.imageUrl)) {
                (img as any).onClick = () => setPdfViewerUrl(img.imageUrl);
            } else {
                const idx = imageUrls.indexOf(img.imageUrl);
                (img as any).onClick = () => {
                    setViewerImages({ urls: imageUrls, index: idx });
                };
            }
        });

        return results;
    }, [query, logEntries, notes, parts, locations, reminders, images, slug, navigate, effectivePreferences, t]);

    const sectionsOrder = useMemo(() => {
        const current = activeTab === 'stats' ? 'expenses' : activeTab;
        const first = current === 'spare-parts' ? 'parts' : current === 'gallery' ? 'gallery' : current === 'notes' ? 'notes' : 'expenses';

        const others = ['expenses', 'parts', 'notes', 'locations', 'reminders', 'gallery'].filter((t: string) => t !== first);
        return [first, ...others];
    }, [activeTab]);

    return (
        <div className="fixed inset-0 bg-transparent z-[100] flex flex-col font-primary overflow-hidden">
            {/* Nav bar with search input - matched with SparePartSelector style */}
            <div className="flex-shrink-0 z-30 non-draggable bg-transparent">
                <div className="max-w-2xl mx-auto flex items-center px-4 h-[68px] gap-3">
                    <div className="relative flex-1 group">
                        <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-blue-500 transition-colors" />
                        <input
                            autoFocus
                            type="text"
                            placeholder={t.search.placeholder}
                            value={localQuery}
                            onChange={(e) => setLocalQuery(e.target.value)}
                            className="w-full pl-12 pr-4 py-2.5 bg-white dark:bg-white/5 border border-gray-200 dark:border-white/10 rounded-xl focus:ring-2 focus:ring-blue-500/50 dark:focus:ring-blue-400/50 focus:border-transparent transition-all text-gray-900 dark:text-gray-100 shadow-sm text-[15px] font-medium"
                        />
                    </div>
                    <button
                        onClick={onClose}
                        className="text-blue-600 dark:text-blue-400 font-bold text-[15px] px-2 py-2 active:opacity-60 transition-opacity whitespace-nowrap"
                    >
                        {t.common.cancel}
                    </button>
                </div>
            </div>

            <div className="flex-1 overflow-y-auto">
                <div className="max-w-2xl mx-auto p-4 space-y-8 pb-32">
                    {!query && !loading && (
                        <div className="text-center py-20 opacity-50">
                            <Search size={48} className="mx-auto mb-4 text-gray-300 dark:text-white/20" />
                            <p className="text-gray-500 dark:text-gray-400 font-bold">{t.search.typeToSearch}</p>
                        </div>
                    )}

                    {query && sectionsOrder.map((sectionKey: string) => {
                        const results = searchResults[sectionKey as keyof typeof searchResults];
                        if (!results || results.length === 0) return null;

                        const sectionTitle = sectionKey === 'expenses' ? t.expenses.title :
                            sectionKey === 'parts' ? t.spareParts.title :
                                sectionKey === 'notes' ? t.notes.title :
                                    sectionKey === 'locations' ? (t.locations as any)?.title || 'Locations' :
                                        sectionKey === 'reminders' ? (t.reminders as any)?.title || 'Reminders' :
                                            t.gallery.title;

                        return (
                            <div key={sectionKey} className="space-y-2">
                                <div className="flex items-center justify-between px-1">
                                    <h3 className={theme.typography.sectionTitle}>
                                        {sectionTitle}
                                    </h3>
                                </div>
                                <div className={styles.card}>
                                    <div className="divide-y divide-gray-100 dark:divide-white/5">
                                        {results.map((item: any) => {
                                            if (sectionKey === 'expenses') {
                                                return (
                                                    <LogEntryItem
                                                        key={item.id}
                                                        entry={item}
                                                        slug={slug}
                                                        isAdminView={outletContext.isAdminView}
                                                        isPublicView={outletContext.isReadOnly}
                                                        shareLocationsEnabled={selectedCar?.share_locations}
                                                        effectivePreferences={effectivePreferences}
                                                        forceShowYear={true}
                                                    />
                                                );
                                            }
                                            if (sectionKey === 'parts') {
                                                return (
                                                    <SparePartItem
                                                        key={item.id}
                                                        part={item}
                                                        slug={slug}
                                                        isAdminView={outletContext.isAdminView}
                                                        compact={true}
                                                    />
                                                );
                                            }
                                            if (sectionKey === 'notes') {
                                                return (
                                                    <NoteItem
                                                        key={item.id}
                                                        note={{
                                                            ...item,
                                                            imageCount: item.note_images?.length || 0,
                                                            partCount: item.note_spare_parts?.length || 0,
                                                            updated_at: item.updated_at || item.created_at
                                                        }}
                                                        slug={slug}
                                                        isAdminView={outletContext.isAdminView}
                                                        compact={true}
                                                        carIsPublic={selectedCar?.is_public}
                                                        forceShowYear={true}
                                                    />
                                                );
                                            }
                                            if (sectionKey === 'gallery') {
                                                return (
                                                    <GalleryItem
                                                        key={item.id}
                                                        item={item}
                                                    />
                                                );
                                            }

                                            if (sectionKey === 'locations') {
                                                return (
                                                    <LocationItem
                                                        key={item.id}
                                                        location={item}
                                                        slug={slug}
                                                        compact={true}
                                                        onClick={() => navigate(`/${slug}/locations/${item.id}`)}
                                                    />
                                                );
                                            }
                                            if (sectionKey === 'reminders') {
                                                return (
                                                    <ReminderItem
                                                        key={item.id}
                                                        reminder={item}
                                                        compact={true}
                                                        onClick={() => {
                                                            const prefix = outletContext.isAdminView ? '/admin' : '';
                                                            if (item.note_id) {
                                                                navigate(`${prefix}/${slug}/notes/${item.note_id}`);
                                                            } else if (item.spare_part_id) {
                                                                navigate(`${prefix}/${slug}/parts/${item.spare_part_id}`);
                                                            } else {
                                                                navigate(`${prefix}/${slug}/reminders/${item.id}`);
                                                            }
                                                        }}
                                                    />
                                                );
                                            }

                                            return (
                                                <button
                                                    key={`${item.type}-${item.id}`}
                                                    onClick={item.onClick}
                                                    className="w-full flex items-center gap-4 p-4 text-left hover:bg-gray-50 dark:hover:bg-white/5 active:bg-gray-100 dark:active:bg-white/10 transition-all group"
                                                >
                                                    <div
                                                        className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 overflow-hidden"
                                                        style={{ backgroundColor: `${item.color}15` }}
                                                    >
                                                        {item.imageUrl && !isPdfFile(item.imageUrl) ? (
                                                            <CachedImage
                                                                src={item.imageUrl}
                                                                alt={item.title}
                                                                className="w-full h-full object-cover"
                                                            />
                                                        ) : item.imageUrl && isPdfFile(item.imageUrl) ? (
                                                            <FileText size={20} className="text-red-500 dark:text-red-400" />
                                                        ) : (
                                                            <DynamicIcon name={item.icon} size={20} color={item.color} />
                                                        )}
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <div className="flex items-center justify-between gap-2">
                                                            <h4 className={theme.typography.itemTitle}>
                                                                {item.title}
                                                            </h4>
                                                        </div>
                                                        <div className={`flex items-center gap-3 mt-1.5 ${theme.typography.itemSubtitle} flex-wrap`}>
                                                            {item.date && (
                                                                <span className="font-bold text-gray-400 uppercase tracking-widest whitespace-nowrap">
                                                                    {new Date(item.date).toLocaleDateString(language, { day: '2-digit', month: 'short', year: 'numeric' })}
                                                                </span>
                                                            )
                                                            }
                                                            {item.subtitle && (
                                                                <>
                                                                    <span className="text-gray-300 dark:text-white/10 hidden sm:inline">|</span>
                                                                    <span className="truncate italic max-w-[200px] text-gray-500 dark:text-gray-400">
                                                                        {item.subtitle}
                                                                    </span>
                                                                </>
                                                            )}
                                                        </div>
                                                    </div>
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>
                        );
                    })}

                    {query && Object.values(searchResults).every((arr: any) => arr.length === 0) && (
                        <div className="text-center py-20 opacity-50">
                            <p className="text-gray-500 dark:text-gray-400 font-bold italic">{t.search.noResults.replace('{query}', query)}</p>
                        </div>
                    )}
                </div>
            </div>

            {viewerImages && (
                <ImageViewer
                    images={viewerImages.urls}
                    initialIndex={viewerImages.index}
                    onClose={() => setViewerImages(null)}
                    relatedLinks={viewerImages.urls.map(url => {
                        const img = images.find(i => i.image_url === url);
                        if (!slug || !img || !img.type) return undefined;
                        let matchUrl = '';
                        let label = '';
                        if (img.type === 'expense') {
                            matchUrl = `/${slug}/expenses/${img.log_entry_id}`;
                            label = (t.expenses as any).openExpense || 'Open Expense';
                        } else if (img.type === 'note') {
                            matchUrl = `/${slug}/notes/${img.note_id}`;
                            label = (t.notes as any).openNote || 'Open Note';
                        } else {
                            matchUrl = `/${slug}/parts/${img.spare_part_id}`;
                            label = (t.spareParts as any).openPart || 'Open Part';
                        }
                        return { url: matchUrl, label };
                    })}
                />
            )}
            {pdfViewerUrl && (() => {
                const img = images.find(i => i.image_url === pdfViewerUrl);
                let relatedLink = undefined;
                if (slug && img && img.type) {
                    if (img.type === 'expense') {
                        relatedLink = { url: `/${slug}/expenses/${img.expense_id}`, label: (t.expenses as any).openExpense || 'Open Expense' };
                    } else if (img.type === 'note') {
                        relatedLink = { url: `/${slug}/notes/${img.note_id}`, label: (t.notes as any).openNote || 'Open Note' };
                    } else {
                        relatedLink = { url: `/${slug}/parts/${img.spare_part_id}`, label: (t.spareParts as any).openPart || 'Open Part' };
                    }
                }
                return (
                    <PdfViewer
                        url={pdfViewerUrl}
                        onClose={() => setPdfViewerUrl(null)}
                        relatedLink={relatedLink}
                    />
                );
            })()}
        </div >
    );
}
